import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setChannelId } from "../../store/channel";
import { useGetAllCategoriesQuery } from "../../services/modules/category";
import { useGetAllNetworksQuery } from "../../services/modules/network";
import { useCreateChannelMutation } from "../../services/modules/channel";
import { useCreateVideoMutation } from "../../services/modules/video";
import { toast } from "react-toastify";
import { REACT_APP_YOUTUBE_API_KEY } from "../../constants";
import { selectVar } from "../../store/conf/confSlice.js";

const ChannelUploader = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token_youtube = useSelector(selectVar("_TOKEN_YOUTUBE", REACT_APP_YOUTUBE_API_KEY));

  const [channelUrl, setChannelUrl] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [networkIds, setNetworkIds] = useState([]); // multi reti
  const [isProcessing, setIsProcessing] = useState(false);

  // Stato per il wizard/modale
  const [modalOpen, setModalOpen] = useState(false);
  const [videosQueue, setVideosQueue] = useState([]);     // tutti i video trovati
  const [currentIdx, setCurrentIdx] = useState(0);        // indice corrente
  const [channelDbId, setChannelDbId] = useState(null);   // id canale creato nel DB
  const [autoSkipDuplicates, setAutoSkipDuplicates] = useState(true);
  const [itemSaving, setItemSaving] = useState(false);
  const [itemError, setItemError] = useState("");         // errore sull’item corrente
  const [stats, setStats] = useState({ added: 0, skipped: 0, duplicates: 0, errors: 0 });

  const { data: categoriesData } = useGetAllCategoriesQuery();
  const { data: networksData } = useGetAllNetworksQuery();
  const [createChannel] = useCreateChannelMutation();
  const [createVideo] = useCreateVideoMutation();

  const categories = categoriesData?.data?.result || [];
  const networks = networksData?.data?.result || [];

  const apiKey = token_youtube;


  const extractChannelIdFromUrl = async (url) => {
  
    if (!apiKey) throw new Error("YouTube API Key is not configured.");

    if (url.includes("/channel/")) {
      return url.split("/channel/")[1].split(/[/?#]/)[0];
    }
    if (url.includes("/user/")) {
      const username = url.split("/user/")[1].split(/[/?#]/)[0];
      const res = await fetch(
        `https://www.googleapis.com/youtube/v3/channels?part=id&forUsername=${username}&key=${apiKey}`
      );
      const data = await res.json();
      console.log("Risposta API:", data);
      return data.items?.[0]?.id || null;
    }
    if (url.includes("/c/")) {
      const customName = url.split("/c/")[1].split(/[/?#]/)[0];
      const res = await fetch(
        `https://www.googleapis.com/youtube/v3/search?part=snippet&q=${customName}&type=channel&key=${apiKey}`
      );
      const data = await res.json();
      console.log("Risposta API:", data);
      return data.items?.[0]?.snippet?.channelId || null;
    }
    if (url.includes("@")) {
      const handle = url.split("@")[1].split(/[/?#]/)[0];
      const res = await fetch(
        `https://www.googleapis.com/youtube/v3/search?part=snippet&q=${handle}&type=channel&key=${apiKey}`
      );
      const data = await res.json();
      console.log("Risposta API:", data);
      return data.items?.[0]?.snippet?.channelId || null;
    }
    return null;
  };

  // 🔁 Fetch di TUTTI i video dalla playlist "uploads"
  const fetchAllYouTubeChannelVideos = async (apiKey, channelId) => {
    const channelRes = await fetch(
      `https://www.googleapis.com/youtube/v3/channels?part=contentDetails&id=${channelId}&key=${apiKey}`
    );
    const channelData = await channelRes.json();
    const uploadsId = channelData.items?.[0]?.contentDetails?.relatedPlaylists?.uploads;
    if (!uploadsId) throw new Error("Could not get uploads playlist.");

    let allVideos = [];
    let nextPageToken = undefined;
    console.log("Fetching videos from uploads playlist:", uploadsId, apiKey);
    do {
      const url = new URL("https://www.googleapis.com/youtube/v3/playlistItems");
      url.searchParams.set("part", "snippet,contentDetails");
      url.searchParams.set("maxResults", "50");
      url.searchParams.set("playlistId", uploadsId);
      if (nextPageToken) url.searchParams.set("pageToken", nextPageToken);
      url.searchParams.set("key", apiKey);

      const playlistRes = await fetch(url);
      const playlistData = await playlistRes.json();

      const items = (playlistData.items || []).map((item) => ({
        title: item.snippet.title,
        description: item.snippet.description || "",
        url: `https://www.youtube.com/watch?v=${item.contentDetails.videoId}`,
        thumbnail: item.snippet.thumbnails?.medium?.url || "",
      }));

      allVideos = allVideos.concat(items);
      nextPageToken = playlistData.nextPageToken || undefined;
    } while (nextPageToken);

    return allVideos;
  };

  const handleNetworkChange = (e) => {
    const values = Array.from(e.target.selectedOptions).map((o) => Number(o.value));
    setNetworkIds(values);
  };

  // Avvio: risolve canale YT, fetch TUTTI i video, crea il canale nel DB, poi apre la modale
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!channelUrl || !categoryId || networkIds.length === 0) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      if (!apiKey) {
        toast.error("YouTube API Key is not configured.");
        return;
      }

      setIsProcessing(true);
      console.log("channelUrl inserita:", channelUrl);
      const youtubeChannelId = await extractChannelIdFromUrl(channelUrl);
      console.log("channelUrl risolto:", youtubeChannelId);
      if (!youtubeChannelId) throw new Error("Could not resolve channel ID.");

      // 1) Prende TUTTI i video del canale
      const allVideos = await fetchAllYouTubeChannelVideos(apiKey, youtubeChannelId);
      if (!allVideos.length) throw new Error("No videos found for this channel.");

      // 2) Crea/Registra il canale nella tua app
      const channelRes = await createChannel({
        name: channelUrl,
        description: `Auto-created from: ${channelUrl}`,
      }).unwrap();

      const finalChannelId = channelRes.data.result.id;
      setChannelDbId(finalChannelId);
      dispatch(setChannelId({ url: channelUrl, id: finalChannelId }));

      toast.info(`Canale creato. Trovati ${allVideos.length} video.`);

      // 3) Prepara la coda e apri la modale
      setVideosQueue(allVideos);
      setCurrentIdx(0);
      setStats({ added: 0, skipped: 0, duplicates: 0, errors: 0 });
      setItemError("");
      setModalOpen(true);
    } catch (err) {
      console.error("Error preparing channel:", err, err?.data);
      toast.error(err?.data?.message || err.message || "Failed to process channel.");
    } finally {
      setIsProcessing(false);
    }
  };

  // Helpers modale
  const isDone = currentIdx >= videosQueue.length;
  const currentVideo = !isDone ? videosQueue[currentIdx] : null;
  const progressPct = videosQueue.length
    ? Math.round((currentIdx / videosQueue.length) * 100)
    : 0;

  const goNext = () => {
    setItemError("");
    setCurrentIdx((i) => i + 1);
  };

  const onSkip = () => {
    setStats((s) => ({ ...s, skipped: s.skipped + 1 }));
    goNext();
  };

  const onAddCurrent = async () => {
    if (!currentVideo || !channelDbId) return;
    setItemSaving(true);
    setItemError("");

    const payload = {
      ...currentVideo,
      categoryIds: [Number(categoryId)],
      networkIds: networkIds.map(Number),
      channelId: channelDbId,
      type: "youtube",
    };

    try {
      await createVideo(payload).unwrap();
      setStats((s) => ({ ...s, added: s.added + 1 }));
      goNext();
    } catch (err) {
      const msg = err?.data?.message || err.message || "Errore sconosciuto";
      const isDuplicate =
        msg.includes("Unique constraint failed") || msg.toLowerCase().includes("already exists");

      if (isDuplicate) {
        setStats((s) => ({ ...s, duplicates: s.duplicates + 1 }));
        if (autoSkipDuplicates) {
          goNext();
        } else {
          setItemError("Video duplicato rilevato.");
        }
      } else {
        setStats((s) => ({ ...s, errors: s.errors + 1 }));
        setItemError(msg);
      }
    } finally {
      setItemSaving(false);
    }
  };

  const closeWizard = () => {
    setModalOpen(false);
    // se vuoi tornare alla lista canali/video alla fine:
    navigate("/admin/channel");
  };

  return (
    <div className="max-w-xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6 text-center">Add YouTube Channel</h2>
      <form onSubmit={handleSubmit} className="bg-white p-8 rounded shadow-md space-y-6">
        <input
          type="url"
          placeholder="YouTube Channel URL"
          value={channelUrl}
          onChange={(e) => setChannelUrl(e.target.value)}
          className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
          required
        />

        <select
          name="categoryId"
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
          required
        >
          <option value="">Seleziona Categoria</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>

        {/* Multi-select networks */}
        <div>
          <label className="block text-sm font-medium mb-2">Select Networks</label>
          <select
            name="networkIds"
            multiple
            value={networkIds.map(String)}
            onChange={handleNetworkChange}
            className="border p-2 w-full rounded h-40 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            required
          >
            {networks.map((net) => (
              <option key={net.id} value={net.id}>
                {net.name}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">Tip: usa Ctrl/Cmd per selezioni multiple.</p>
          <div className="text-xs text-gray-600 mt-1">
            Selezionate: <b>{networkIds.length}</b> / {networks.length}
          </div>
        </div>

        <button
          type="submit"
          disabled={isProcessing}
          className="bg-black text-white py-2 w-full rounded hover:bg-gray-800 disabled:opacity-50"
        >
          {isProcessing ? "Sto pensando..." : "Caricamento guidato"}
        </button>
      </form>

      {/* MODALE WIZARD */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* backdrop */}
          <div className="absolute inset-0 bg-black/40" onClick={closeWizard} />

          <div className="relative bg-white w-[720px] max-w-[95vw] rounded-xl shadow-xl p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                Importa Video YouTube — {isDone ? "Completato" : `Video ${currentIdx + 1} di ${videosQueue.length}`}
              </h3>
              <button
                onClick={closeWizard}
                className="text-gray-500 hover:text-gray-700 text-xl leading-none"
                aria-label="Chiudi"
                title="Chiudi"
              >
                ×
              </button>
            </div>

            {/* Progress */}
            <div className="mb-4">
              <div className="h-2 w-full bg-gray-200 rounded">
                <div
                  className="h-2 bg-red-500 rounded"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <div className="mt-2 text-xs text-gray-600">
                Avanzamento: <b>{progressPct}%</b> — Aggiunti: <b>{stats.added}</b>, Saltati:{" "}
                <b>{stats.skipped}</b>, Duplicati: <b>{stats.duplicates}</b>, Errori:{" "}
                <b>{stats.errors}</b>
              </div>
            </div>

            {/* Body */}
            {isDone ? (
              <div className="text-center py-6">
                <p className="text-lg font-medium mb-2">Import completato 🎉</p>
                <p className="text-sm text-gray-600">
                  Aggiunti: <b>{stats.added}</b> · Saltati: <b>{stats.skipped}</b> · Duplicati:{" "}
                  <b>{stats.duplicates}</b> · Errori: <b>{stats.errors}</b>
                </p>
                <div className="mt-6 flex gap-3 justify-center">
                  <button
                    className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                    onClick={() => navigate("/admin/channel")}
                  >
                    Vai ai Canali
                  </button>
                  <button
                    className="px-4 py-2 rounded bg-black text-white hover:bg-gray-800"
                    onClick={closeWizard}
                  >
                    Chiudi
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-[160px_1fr] gap-4">
                <img
                  src={currentVideo?.thumbnail || "https://via.placeholder.com/160x90"}
                  alt="thumb"
                  className="w-[160px] h-[90px] object-cover rounded"
                />
                <div>
                  <div className="font-medium mb-1">{currentVideo?.title}</div>
                  <div className="text-xs text-gray-500 mb-2 break-words">
                    {currentVideo?.url}
                  </div>
                  <div className="text-sm max-h-28 overflow-auto whitespace-pre-wrap border rounded p-2 bg-gray-50">
                    {currentVideo?.description || "—"}
                  </div>

                  <div className="mt-3 flex items-center gap-4">
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
                      <input
                        type="checkbox"
                        className="accent-red-600"
                        checked={autoSkipDuplicates}
                        onChange={(e) => setAutoSkipDuplicates(e.target.checked)}
                      />
                      Salta automaticamente i duplicati
                    </label>
                    <span className="text-xs text-gray-500">
                      Categoria: <b>{categories.find((c) => c.id === Number(categoryId))?.name || "-"}</b>
                    </span>
                    <span className="text-xs text-gray-500">
                      Reti:{" "}
                      <b>
                        {networkIds
                          .map((id) => networks.find((n) => n.id === Number(id))?.name)
                          .filter(Boolean)
                          .join(", ") || "-"}
                      </b>
                    </span>
                  </div>

                  {itemError && (
                    <div className="mt-3 text-sm text-red-600">
                      {itemError}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Footer azioni */}
            {!isDone && (
              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={onSkip}
                  disabled={itemSaving}
                  className="px-4 py-2 rounded border hover:bg-gray-50"
                >
                  Salta
                </button>
                <button
                  onClick={onAddCurrent}
                  disabled={itemSaving}
                  className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                >
                  {itemSaving ? "Aggiungo..." : "Aggiungi"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChannelUploader;
